import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { useSEO } from "../hooks/useSEO";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useSEO("Sign In | NAKSRA", "Sign in to your NAKSRA account to access your Vedic astrology reports.");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      const next = params.get("next");
      navigate(next || (user.role === "admin" ? "/admin" : "/account"));
    } catch (err) {
      setError(formatApiError(err));
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-[#27272A] bg-[#050507] px-4 py-3 text-sm text-white placeholder-[#52525B] outline-none transition-colors duration-300 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050507] px-6 text-[#F8F9FA]">
      <div className="w-full max-w-md rounded-lg border border-white/10 bg-[#0C0C10] p-10">
        <Link to="/" data-testid="login-logo" className="flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F4F1EA] p-1">
            <img src="/logo.png" alt="NAKSRA logo" className="h-full w-full object-contain" />
          </span>
          <span className="font-heading text-2xl font-semibold tracking-[0.25em]">NAKSRA</span>
        </Link>
        <h1 className="mt-8 font-heading text-3xl font-medium">Welcome back</h1>
        <p className="mt-2 text-sm text-[#A1A1AA]">Sign in to access your reports.</p>
        <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <label htmlFor="email" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Email</label>
            <input id="email" data-testid="login-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@example.com" />
          </div>
          <div>
            <label htmlFor="password" className="mb-2 block text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">Password</label>
            <input id="password" data-testid="login-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="••••••••" />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D] active:scale-[0.98] disabled:opacity-60">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-[#A1A1AA]">
          New here?{" "}
          <Link to={`/register${params.get("next") ? `?next=${params.get("next")}` : ""}`} data-testid="login-register-link" className="text-[#D4AF37] hover:text-[#FBE18D]">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
